/**
 * Navigation structure — v4.8 Spec Section 3.
 * All 11 navigation groups with their pages/modules.
 *
 * Icons use a string key that maps to SVG components in Sidebar.
 * Feature-flag keys are checked before rendering each item.
 */

import type { UserRole } from '@/types';

export interface NavItem {
  readonly key: string;
  readonly label: string;
  readonly path: string;
  readonly icon: string;
  /** Feature flag key — item hidden if flag is disabled */
  readonly featureFlag?: string;
  /** Minimum role required to see this item */
  readonly minRole?: UserRole;
}

export interface NavGroup {
  readonly key: string;
  readonly label: string;
  readonly items: readonly NavItem[];
}

/**
 * Complete navigation structure — 11 groups per v4.8 spec.
 */
export const NAVIGATION: readonly NavGroup[] = [
  {
    key: 'command',
    label: 'COMMAND',
    items: [
      { key: 'dashboard', label: 'Portfolio Dashboard', path: '/', icon: 'grid', featureFlag: 'portfolio_dashboard' },
      { key: 'focus-brief', label: 'Focus Brief', path: '/focus-brief', icon: 'focus', featureFlag: 'focus_engine' },
      { key: 'calendar', label: 'Intelligence Calendar', path: '/calendar', icon: 'calendar' },
    ],
  },
  {
    key: 'pipeline',
    label: 'PIPELINE',
    items: [
      { key: 'acquisitions', label: 'Acquisition Register', path: '/pipeline/acquisitions', icon: 'search' },
      { key: 'due-diligence', label: 'Due Diligence Hub', path: '/pipeline/due-diligence', icon: 'shield' },
      { key: 'feasibility', label: 'Feasibility', path: '/pipeline/feasibility', icon: 'calculator' },
      { key: 'pre-sales', label: 'Pre-Sales', path: '/pipeline/pre-sales', icon: 'tag' },
      { key: 'dev-finance', label: 'Development Finance', path: '/pipeline/finance', icon: 'bank' },
    ],
  },
  {
    key: 'projects',
    label: 'PROJECTS',
    items: [
      { key: 'projects-list', label: 'Projects', path: '/projects', icon: 'building', featureFlag: 'project_workspace' },
    ],
  },
  {
    key: 'finance',
    label: 'FINANCE',
    items: [
      { key: 'budget', label: 'Budget & Costs', path: '/finance/budget', icon: 'dollar', featureFlag: 'budget_costs' },
      { key: 'invoices', label: 'Invoices', path: '/finance/invoices', icon: 'receipt', featureFlag: 'invoices' },
      { key: 'progress-claims', label: 'Progress Claims', path: '/finance/claims', icon: 'clipboard', featureFlag: 'progress_claims' },
      { key: 'contracts', label: 'Contracts', path: '/finance/contracts', icon: 'file-text' },
      { key: 'variations', label: 'Variations', path: '/finance/variations', icon: 'git-branch', featureFlag: 'variations' },
      { key: 'tenders', label: 'Tenders', path: '/finance/tenders', icon: 'inbox' },
      { key: 'retention', label: 'Retention', path: '/finance/retention', icon: 'lock' },
    ],
  },
  {
    key: 'site',
    label: 'SITE & HANDOVER',
    items: [
      { key: 'site-observations', label: 'Site Observations', path: '/site/observations', icon: 'eye', featureFlag: 'site_observations' },
      { key: 'defects', label: 'Defects', path: '/site/defects', icon: 'alert-triangle', featureFlag: 'defects' },
      { key: 'voice-notes', label: 'Voice Notes', path: '/site/voice-notes', icon: 'mic', featureFlag: 'voice_notes' },
      { key: 'rfi', label: 'RFI Register', path: '/site/rfi', icon: 'help-circle' },
      { key: 'sales-units', label: 'Sales & Units', path: '/site/sales', icon: 'home' },
      { key: 'buyers', label: 'Buyers', path: '/site/buyers', icon: 'users' },
    ],
  },
  {
    key: 'documents',
    label: 'DOCUMENTS',
    items: [
      { key: 'doc-library', label: 'Document Library', path: '/documents', icon: 'folder', featureFlag: 'document_library' },
      { key: 'templates', label: 'Templates', path: '/documents/templates', icon: 'copy' },
    ],
  },
  {
    key: 'contacts',
    label: 'CONTACTS',
    items: [
      { key: 'contacts', label: 'Contacts', path: '/contacts', icon: 'user', featureFlag: 'contact_management' },
      { key: 'consultants', label: 'Consultants', path: '/contacts/consultants', icon: 'briefcase' },
      { key: 'contractors', label: 'Contractors', path: '/contacts/contractors', icon: 'hard-hat' },
    ],
  },
  {
    key: 'intelligence',
    label: 'INTELLIGENCE',
    items: [
      { key: 'ai-assistant', label: 'AI Assistant', path: '/intelligence/assistant', icon: 'sparkle', featureFlag: 'ai_assistant_chat' },
      { key: 'stage-prompts', label: 'Stage Prompts', path: '/intelligence/stage-prompts', icon: 'list-checks', featureFlag: 'stage_prompt_engine' },
      { key: 'learnings', label: 'Learnings', path: '/intelligence/learnings', icon: 'lightbulb' },
      { key: 'meeting-minutes', label: 'Meeting Minutes AI', path: '/intelligence/minutes', icon: 'file-audio', featureFlag: 'meeting_minutes_ai' },
    ],
  },
  {
    key: 'reporting',
    label: 'REPORTING',
    items: [
      { key: 'pcg-studio', label: 'PCG Studio', path: '/reporting/pcg', icon: 'presentation', featureFlag: 'pcg_studio' },
      { key: 'custom-reports', label: 'Custom Reports', path: '/reporting/custom', icon: 'bar-chart', featureFlag: 'custom_reports' },
      { key: 'investor-updates', label: 'Investor Updates', path: '/reporting/investor', icon: 'trending-up' },
    ],
  },
  {
    key: 'comms',
    label: 'COMMUNICATIONS',
    items: [
      { key: 'notifications', label: 'Notifications', path: '/comms/notifications', icon: 'bell', featureFlag: 'notifications_in_app' },
      { key: 'broadcasts', label: 'Broadcasts', path: '/comms/broadcasts', icon: 'megaphone', featureFlag: 'broadcasts', minRole: 'director' },
    ],
  },
  {
    key: 'system',
    label: 'SYSTEM',
    items: [
      { key: 'help', label: 'Help Centre', path: '/system/help', icon: 'help' },
      { key: 'feature-flags', label: 'Feature Control', path: '/system/features', icon: 'toggles', minRole: 'director' },
      { key: 'admin', label: 'Admin Panel', path: '/system/admin', icon: 'settings', minRole: 'director' },
      { key: 'connectors', label: 'Connectors', path: '/system/connectors', icon: 'plug', minRole: 'director' },
    ],
  },
];

/**
 * Mobile bottom tab bar — subset of most-used items.
 * Shows at <768px (md breakpoint).
 */
export const MOBILE_TABS: readonly NavItem[] = [
  { key: 'dashboard', label: 'Home', path: '/', icon: 'grid' },
  { key: 'projects-list', label: 'Projects', path: '/projects', icon: 'building' },
  { key: 'notifications', label: 'Alerts', path: '/comms/notifications', icon: 'bell' },
  { key: 'ai-assistant', label: 'AI', path: '/intelligence/assistant', icon: 'sparkle' },
  { key: 'more', label: 'More', path: '/menu', icon: 'menu' },
];
